import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { omit } from "lodash";
import { PriceModifiers, PriceModifierModes } from "@repo/utils";
import { FC_PREFIX, MAT_PREFIX, generatePriceModifierId } from "@repo/structure";
import { useEstimateStructure } from "@tools";
import { DEFAULT_FIXED_PRICE, DEFAULT_PERCENT } from "@tools/EstimateEditor/utils";
import { Button, Dialog, NavigationSwitch, AddButtonFull } from "@components";
import Grid from "@material-ui/core/Grid";
import { PercentSettingContents } from "@tools";
import { PriceModifierTitle, FixedPriceInput, ModeSwitch } from "./components";

export const PriceModifierCreator = observer(() => {
  const structure = useEstimateStructure();
  const { t } = useTranslation();
  
  const [ visible, show ] = useState(false);
  const [ tmpDiscount, setTmpDiscount ] = useState(null);
  const [ tmpOverhead, setTmpOverhead ] = useState(null);
  const [ type, setType ] = useState(PriceModifiers.DISCOUNT);
  
  const availableWorkTypes = useMemo(() => {
    let list = [ ...structure.workTypes ];
    
    if(structure.hasBreakdownFixedPrice)
      list.push({ id: FC_PREFIX, name: t("views.editor.summary_table.fixed_costs")});
    if(structure.hasBreakdownMaterialPrice)
      list.push({ id: MAT_PREFIX, name: t("views.editor.summary_table.materials") });

    return list;
  }, [ structure.workTypes, structure.hasBreakdownFixedPrice, structure.hasBreakdownMaterialPrice ]);
  
  useEffect(() => {
    if(visible) {
      setTmpDiscount(createTmpPriceModifier(PriceModifiers.DISCOUNT));
      setTmpOverhead(createTmpPriceModifier(PriceModifiers.OVERHEAD));
    }
  }, [ visible ]);
  
  const tmpPriceModifier = type === PriceModifiers.DISCOUNT ? tmpDiscount : tmpOverhead;
  
  const handleOpen = async () => {
    show(true);
  }
  
  const handleClose = () => {
    show(false);
    setTmpDiscount(null);
    setTmpOverhead(null);
  }
  
  const addNewPriceModifier = () => {
    structure.addPriceModifier(omit(
      tmpPriceModifier,
      tmpPriceModifier.mode === PriceModifierModes.PERCENT
        ? [ "fixedPrice" ]
        : [ "percent", "ofEntities" ]
    ));
    handleClose();
  }
  
  const createTmpPriceModifier = (type) => {
    const name = t(`views.editor.dialogs.price_modifiers.new.${type}`);
    const oldNameUses = (
      type === PriceModifiers.DISCOUNT ? structure.addedDiscounts : structure.addedOverheads
    ).filter(ov => (
      ov.name.includes(name)
    )).length;
    
    return {
      id: generatePriceModifierId(),
      type,
      mode: PriceModifierModes.PERCENT,
      name: oldNameUses > 0 ? `${name} (${oldNameUses+1})` : name,
      percent: type === PriceModifiers.DISCOUNT ? -DEFAULT_PERCENT : DEFAULT_PERCENT,
      fixedPrice: type === PriceModifiers.DISCOUNT ? -DEFAULT_FIXED_PRICE : DEFAULT_FIXED_PRICE,
      ofEntities: availableWorkTypes.map(wT => wT.id),
      used: true,
    };
  }
  
  const handleTmpPriceModifierChange = (updatedData) => {
    const data = {
      ...tmpPriceModifier,
      ...updatedData
    }
    
    type === PriceModifiers.DISCOUNT
      ? setTmpDiscount(data)
      : setTmpOverhead(data);
  }
  
  const handleSetName = (newName) => {
    handleTmpPriceModifierChange({ name: newName })
  }
  
  const handleSetOf = (ofEntities) => {
    handleTmpPriceModifierChange({ ofEntities })
  }
  
  const handleSetMode = (mode) => {
    handleTmpPriceModifierChange({ mode })
  }
  
  const handleSetPercent = (newPercent) => {
    // Ensure element always uses positive/negative percent values
    const modifiedPercent = type === PriceModifiers.DISCOUNT
      ? -Math.abs(newPercent)
      : Math.abs(newPercent);
    handleTmpPriceModifierChange({ percent: modifiedPercent })
  }
  
  const handleSetFixedPrice = (newValue) => {
    // Ensure element always uses positive/negative percent values
    const modifiedValue = type === PriceModifiers.DISCOUNT
      ? -Math.abs(newValue)
      : Math.abs(newValue);
    handleTmpPriceModifierChange({ fixedPrice: modifiedValue })
  }
  
  const navigationValues = [
    { value: PriceModifiers.DISCOUNT, label: t(`proposal_parts.price_modifiers.${PriceModifiers.DISCOUNT}`) },
    { value: PriceModifiers.OVERHEAD, label: t(`proposal_parts.price_modifiers.${PriceModifiers.OVERHEAD}`) },
  ];
  
  return (
    <>
      <AddButtonFull
        id="addPriceModifier"
        color="secondary"
        onClick={handleOpen}
        name={t("views.editor.dialogs.price_modifiers.create")}
      >
        {t("views.editor.dialogs.price_modifiers.create")}
      </AddButtonFull>
      {
        visible &&
        <Dialog
          open
          onClose={handleClose}
          width={624}
          title={<Grid container alignItems="center">
            <NavigationSwitch
              values={navigationValues}
              value={type}
              onChange={(event, newValue) => setType(newValue)}
              buttonClassName="h-56"
              labelClassName="text-xl font-bold"
              lighter
            />
          </Grid>}
          actions={
            <>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleClose}
              >
                {t("common.close")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={addNewPriceModifier}
              >
                {t(`views.editor.dialogs.price_modifiers.add.${type === PriceModifiers.DISCOUNT ? "discount" : "overhead"}`)}
              </Button>
            </>
          }
          titleContainerClass="p-0 pr-4 items-center"
        >
          {
            tmpPriceModifier &&
            <Grid item container direction="column">
              <PriceModifierTitle
                name={tmpPriceModifier.name}
                type={tmpPriceModifier.type}
                useActions={false}
                setName={handleSetName}
              />
              <ModeSwitch
                value={tmpPriceModifier.mode}
                onChange={handleSetMode}
              />
              {
                tmpPriceModifier.mode === PriceModifierModes.PERCENT
                  ? <PercentSettingContents
                      percent={Math.abs(tmpPriceModifier.percent)}
                      usedWorkTypes={tmpPriceModifier.ofEntities}
                      availableWorkTypes={availableWorkTypes}
                      onPercentChange={handleSetPercent}
                      onSelectWorkType={handleSetOf}
                      hideWorkTypeList
                      allowSelectAllWorkTypes
                      minValue={0.01}
                      maxValue={999}
                      prefix={tmpPriceModifier.type === PriceModifiers.DISCOUNT ? "-" : undefined}
                    />
                  : <FixedPriceInput
                      value={Math.abs(tmpPriceModifier.fixedPrice)}
                      onChange={handleSetFixedPrice}
                      prefix={tmpPriceModifier.type === PriceModifiers.DISCOUNT ? "-" : undefined}
                    />
              }
            </Grid>
          }
        </Dialog>
      }
    </>
  );
});